import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { SERVICES_CONFIG, COMPANY_CONFIG } from '../config/site';

export function ServicesPage() {
  return (
    <>
      <SEOHead
        title="Software Engineering Services | Safaza Technologies, Inc."
        description="Explore custom software development, fintech software engineering, SaaS product development, and API integration services by Safaza Technologies, Inc."
      />

      {/* Hero Header */}
      <section className="pt-16 pb-16 sm:pt-24 sm:pb-20 bg-white border-b border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0D66E4]/8 border border-[#0D66E4]/20 text-[11px] font-mono font-medium text-[#0D66E4] mb-4">
              <span>TECHNICAL CAPABILITIES</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#101828] mb-6 leading-[1.15]">
              Software engineering built to solve real business problems
              <span className="text-[#0D66E4]">.</span>
            </h1>
            <p className="text-lg text-[#667085] leading-relaxed">
              Safaza Technologies, Inc. designs, builds, and maintains custom software applications
              for businesses. From high-reliability web and mobile applications to complex
              fintech software architectures and enterprise API integrations, we deliver dependable
              codebases that scale.
            </p>
          </div>
        </div>
      </section>

      {/* Core Services Grid */}
      <section className="py-20 sm:py-28 bg-[#F6F8FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#101828] mb-4">
              Our Core Services
              <span className="text-[#0D66E4]">.</span>
            </h2>
            <p className="text-base text-[#667085]">
              We offer end-to-end software engineering capabilities grounded in clean architecture,
              rigorous security, and measurable business outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {SERVICES_CONFIG.map((service) => (
              <div key={service.id} className="double-bezel h-full flex flex-col">
                <div className="double-bezel-inner p-8 sm:p-10 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="inline-block px-3 py-1 rounded-full bg-[#0D66E4]/10 text-[#0D66E4] text-xs font-mono font-semibold mb-4">
                      {service.category}
                    </div>

                    <h3 className="text-2xl font-bold text-[#101828] mb-2">
                      {service.title}
                    </h3>

                    <p className="text-sm font-medium text-[#667085] mb-5">
                      {service.tagline}
                    </p>

                    <p className="text-sm text-[#475467] leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="space-y-4 pt-6 border-t border-[#E4E7EC]">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wider text-[#101828] mb-3">
                          Key Deliverables
                        </h4>
                        <ul className="space-y-2 text-xs sm:text-sm text-[#475467]">
                          {service.deliverables.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#0D66E4] mt-1.5 shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="pt-4 border-t border-[#E4E7EC]/60">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-[#101828] mb-3">
                          Technical Competencies
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {service.capabilities.map((cap, idx) => (
                            <span
                              key={idx}
                              className="px-2.5 py-1 rounded-lg bg-[#F6F8FB] border border-[#E4E7EC] text-[11px] font-medium text-[#475467]"
                            >
                              {cap}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#E4E7EC] flex items-center justify-between">
                    <Link
                      to={`/contact?type=${encodeURIComponent('Custom software development')}&subject=${encodeURIComponent(service.title)}`}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#101828] hover:text-[#0D66E4] transition-colors"
                    >
                      <span>Discuss a project</span>
                      <svg
                        className="w-4 h-4 text-[#0D66E4]"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models & Commercial Clarity */}
      <section className="py-20 sm:py-28 bg-white border-b border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#101828] mb-4">
              Commercial Engagement Models
              <span className="text-[#0D66E4]">.</span>
            </h2>
            <p className="text-base text-[#667085]">
              We work with startups, growth businesses, and enterprise teams under clear, milestone-driven
              software development agreements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 rounded-2xl bg-[#F6F8FB] border border-[#E4E7EC] flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono font-bold text-[#0D66E4] block mb-2">MODEL 01</span>
                <h3 className="text-xl font-bold text-[#101828] mb-3">Milestone-Based Projects</h3>
                <p className="text-sm text-[#667085] leading-relaxed mb-6">
                  Ideal for discrete applications, greenfield product builds, and API integrations with clear scope, defined architecture, and fixed deliverables.
                </p>
              </div>
              <div className="text-xs text-[#475467] font-medium pt-4 border-t border-[#E4E7EC]">
                Fixed-price milestone billing &middot; Phased release schedule
              </div>
            </div>

            <div className="p-8 rounded-2xl bg-[#F6F8FB] border border-[#E4E7EC] flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono font-bold text-[#0D66E4] block mb-2">MODEL 02</span>
                <h3 className="text-xl font-bold text-[#101828] mb-3">Dedicated Engineering Teams</h3>
                <p className="text-sm text-[#667085] leading-relaxed mb-6">
                  For growing organizations requiring dedicated technical teams to accelerate roadmap execution, build internal tools, or refactor legacy codebases.
                </p>
              </div>
              <div className="text-xs text-[#475467] font-medium pt-4 border-t border-[#E4E7EC]">
                Monthly retainer billing &middot; Scalable engineering capacity
              </div>
            </div>

            <div className="p-8 rounded-2xl bg-[#F6F8FB] border border-[#E4E7EC] flex flex-col justify-between">
              <div>
                <span className="text-xs font-mono font-bold text-[#0D66E4] block mb-2">MODEL 03</span>
                <h3 className="text-xl font-bold text-[#101828] mb-3">Software Support &amp; Maintenance</h3>
                <p className="text-sm text-[#667085] leading-relaxed mb-6">
                  Ongoing maintenance retainers ensuring software platforms remain secure, high-performing, and updated with evolving third-party API dependencies.
                </p>
              </div>
              <div className="text-xs text-[#475467] font-medium pt-4 border-t border-[#E4E7EC]">
                SLA commitments &middot; Proactive security &amp; dependency patching
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clear Scope & Regulatory Clarification */}
      <section className="py-16 sm:py-20 bg-[#F6F8FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 rounded-2xl bg-white border border-[#E4E7EC] max-w-4xl mx-auto">
            <h3 className="text-lg font-bold text-[#101828] mb-3">
              Technical Scope &amp; Professional Boundaries
            </h3>
            <p className="text-sm text-[#667085] leading-relaxed mb-4">
              Safaza Technologies, Inc. is a software development and technology engineering company.
              Our technical services encompass software programming, system design, API integration,
              and SaaS infrastructure engineering.
            </p>
            <p className="text-sm text-[#667085] leading-relaxed">
              We do not provide banking, financial custodial, payment transmission, money transfer,
              currency exchange, or licensed financial advisory services. When we build software for
              fintech clients, our role is strictly that of a software engineering contractor delivering
              code and technical infrastructure to the client’s specifications.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-28 bg-[#101828] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
              Have a software project to build?
            </h2>
            <p className="text-base text-[#98A2B3] leading-relaxed mb-8">
              Reach out to {COMPANY_CONFIG.legalName} to discuss technical requirements, architectural
              planning, or team engagement.
            </p>
            <Link
              to="/contact?type=Custom%20software%20development"
              className="inline-flex items-center gap-3 px-8 py-4 text-base font-semibold text-white bg-[#0D66E4] hover:bg-[#0B56C2] active:scale-[0.98] rounded-full transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <span>Request a software consultation</span>
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
